import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';

import { ActivityLogState } from './types';
import { GetMostRecentActivityLog, LoadActivityLog } from '@/connection/activity-log/get-activity-log';
import { LoadActivityLogOptions } from '@/connection/activity-log/get-activity-log';
import { RootState } from '@/store/main';

export const reducers = (create: ReducerCreators<ActivityLogState>) => ({
  fetchMostRecentActivity: create.asyncThunk(
    async ({ channel, projectUuid }: { channel: number; projectUuid: string }) => {
      const response = await GetMostRecentActivityLog(channel, projectUuid);
      return response;
    },
    {
      fulfilled: (state, action) => {
        state.mostRecentActivity = action.payload.activities[0] || null;
        state.loadingActivityLog = false;
      },
      pending: (state) => {
        state.loadingActivityLog = true;
      },
      rejected: (state, action) => {
        state.loadingActivityLog = false;
        console.error(action.error);
      },
    }
  ),
  fetchActivityLog: create.asyncThunk(
    async (
      { channel, projectUuid, opts }: { channel: number; projectUuid: string; opts?: LoadActivityLogOptions },
      { getState }
    ) => {
      const state = (getState() as RootState).activityLog as ActivityLogState;

      if (state.noMoreActivities) {
        return { activities: [], next: undefined };
      }

      const response = await LoadActivityLog(channel, projectUuid, { ...(opts || {}), published: true });
      return response;
    },
    {
      options: {
        condition: (_, { getState }) => {
          const state = getState() as RootState;
          return !state.activityLog.loadingActivityLog;
        },
      },
      fulfilled: (state, action) => {
        state.activityLogCursor = action.payload.next ?? state.activityLogCursor;

        // Ensure no duplicates
        const activityMap = state.activityLog?.reduce(
          (acc, cur) => ({
            ...acc,
            [cur.uuid]: cur,
          }),
          {} as Record<string, (typeof action.payload.activities)[0]>
        );
        const newActivities = action.payload.activities.filter((activity) => !activityMap?.[activity.uuid]);
        const updatedActivityLog = [...(state.activityLog || []), ...newActivities].toSorted(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        state.activityLog = updatedActivityLog;

        // If the next cursor already exists in the activity log, it means we've reached the end of the log.
        // This is mostly used in conjuction with setMoreActivitiesAvailable, where the cursor is reset because
        // the log is expected to have new entries due to project changes.
        const nextAlreadyExists = !!updatedActivityLog.find((activity) => activity.timestamp_id === action.payload.next)
          ?.uuid;

        if (!action.payload.next || nextAlreadyExists) {
          state.noMoreActivities = true;
        }

        state.activityLogCursor = nextAlreadyExists ? state.activityLogCursor : action.payload.next;
      },
      pending: (state) => {
        state.loadingActivityLog = true;
      },
      rejected: (_, action) => {
        console.error(action.error);
      },
      settled: (state) => {
        state.loadingActivityLog = false;
      },
    }
  ),
  setShowActivityLogModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showActivityLogModal = action.payload;
  }),
  setMoreActivitiesAvailable: create.reducer((state) => {
    state.noMoreActivities = false;
    state.activityLogCursor = undefined;
  }),
  clearActivityLog: create.reducer((state) => {
    state.activityLog = null;
    state.noMoreActivities = false;
    state.activityLogCursor = undefined;
    state.loadingActivityLog = false;
  }),
});
